import BrowserTranscriberProvider from './providers/BrowserTranscriberProvider';
import DeepgramTranscriberProvider from './providers/DeepgramTranscriberProvider';
import isMobile from '../../../isMobile';

export default class Transcriber {
    constructor({
        startBtnSelector = "#livechat_start_transcriber",
        stopBtnSelector = "#livechat_stop_transcriber",
        contentAreaSelector = "#livechat_transcriber_content",
        punctuate = true,
        interim = true,
        context,
        debug = false,
        elements = {}
    } = {}) {
        const apiKey = env.DEEPGRAM_API_KEY || "";
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const hasBrowserRecognition = !!SpeechRecognition;

        // Validate availability of any transcription method
        if (!apiKey && !hasBrowserRecognition) {
            throw new Error(
                "No transcription method available: Deepgram key missing and browser SpeechRecognition unsupported"
            );
        }

        // Ensure valid DOM context is provided
        if (!context || !context.querySelector) {
            throw new Error("Transcriber: Invalid or missing DOM context.");
        }

        this.apiKey = apiKey;
        this.debug = debug;
        this.context = context;
        this.elements = elements;

        // Select DOM elements for control and output
        this.startBtn = context.querySelector(startBtnSelector);
        this.stopBtn = context.querySelector(stopBtnSelector);
        this.contentArea = context.querySelector(contentAreaSelector);
        this.listeningParent = this.stopBtn?.parentElement;

        if (!this.startBtn || !this.stopBtn) {
            console.warn("Transcriber: Start or stop button not found.");
            return;
        }

        this.startBtn.classList.add("livechat_start_transcriber_available");

        // Transcription state
        this.transcriptText = "";
        this.transcriptCallback = () => { };
        this.stopCallback = () => { };

        // Choose the appropriate provider based on availability
        this.provider = hasBrowserRecognition
            ? new BrowserTranscriberProvider({ interim, debug })
            : new DeepgramTranscriberProvider({ apiKey, punctuate, interim, debug });

        if (isMobile()) {
            this.provider = new DeepgramTranscriberProvider({ apiKey, punctuate, interim, debug });
        }
        // Attach event listeners
        // this.provider.on('transcript', this._handleTranscript.bind(this));
        // this.provider.on('transcript-interim', this._handleTranscript.bind(this));
        this.provider.on('transcript-interim', (text) =>
            this._handleTranscript(text, { isFinal: false })
        );
        this.provider.on('transcript', (text) =>
            this._handleTranscript(text, { isFinal: true })
        );

        this.provider.on('listening', this._handleListeningClass.bind(this));
        this.provider.on('stop', this._handleStop.bind(this));
        this.provider.on('error', this._handleError.bind(this));

        // Assign click handlers to start and stop buttons
        this.startBtn.onclick = this.start.bind(this);
        this.stopBtn.onclick = this.stop.bind(this);
    }

    // Update any text input fields with the final transcript on stop
    _stopHandler = (finalTranscript) => {
        const { textboxSelector, autoCompleteInput } = this.elements;
        const txtBox = this.context.querySelector(textboxSelector);
        const autoComplete = this.context.querySelector(autoCompleteInput);

        [txtBox, autoComplete].forEach((element) => {
            if (element) {
                element.value = element.value.length
                    ? `${element.value} ${finalTranscript}`
                    : finalTranscript;
                // Dispatch synthetic input event to notify bindings
                element.dispatchEvent(new Event('input', { bubbles: true }));

                // move caret to end (after autocomplete updates)
                setTimeout(() => {
                    element.focus();
                    const len = element.value.length;
                    if (element.setSelectionRange) {
                        element.setSelectionRange(len, len);
                    }
                }, 0);
            }
        });
    }

    _handleListeningClass(isListening) {
        console.log(isListening ? 'yesss' : 'noooo');
        if (isListening) this.stopBtn.classList.add('listening')
        else this.stopBtn.classList.remove('listening');
    }
    // Handles incoming transcript data from the provider
    _handleTranscript(text, { isFinal = false } = {}) {
        if (!text) return;

        const incoming = text.trim();

        // Always show interim live
        if (!isFinal) {
            if (this.contentArea) {
                this.contentArea.textContent = this.transcriptText + incoming;
                // scroll to bottom so last line is visible
                this.contentArea.scrollTop = this.contentArea.scrollHeight;
            }
            this.listeningParent?.classList.add("transcript-added");
            if (this.debug) console.log("[Transcriber] Interim:", text);
            return;
        }
        if (isFinal) {
            this.transcriptText += incoming + " ";
            if (this.contentArea) {
                this.contentArea.textContent = this.transcriptText;
            }

            if (this.debug) console.log("[Transcriber] Final:", text);
            this.transcriptCallback(text);
            return;
        }
    }

    // Called when transcription is stopped
    _handleStop() {
        this.stopBtn.classList.remove("listening-started");
        this.listeningParent?.classList.remove("listening", "transcript-added");
        this.stopBtn.classList.remove('listening');

        if (this.debug) console.log("[Transcriber] Stopped. Final:", this.transcriptText);

        const finalTranscript = this.transcriptText.trim();
        this._stopHandler(finalTranscript);
        this.stopCallback(finalTranscript);
    }

    // Handles provider errors.
    _handleError(err) {
        console.error("[Transcriber] Provider error:", err);
        this._handleStop();
    }

    // Starts transcription and handles UI state changes
    async start() {
        // Prevent starting if pointer-events are disabled (e.g. during typing)
        if (this.startBtn && getComputedStyle(this.startBtn).pointerEvents === 'none') {
            console.warn("Start blocked due to typing disabled");
            return;
        }

        try {
            this.transcriptText = "";
            if (this.contentArea) this.contentArea.textContent = "";

            this.listeningParent?.classList.add("listening");
            this.stopBtn.classList.add("listening-started");

            await this.provider.start(); // Delegate to provider
        } catch (err) {
            console.error("Transcriber: Error starting provider", err);
        }
    }

    // Stops transcription through the provider
    stop() {
        try {
            this.provider.stop();
        } catch (err) {
            console.error("Transcriber: Error stopping provider", err);
        }
    }

    // Register a callback for receiving transcripts
    onTranscript(cb) {
        if (typeof cb === "function") {
            this.transcriptCallback = cb;
        }
    }

    // Register a callback to be called when transcription ends
    onStop(cb) {
        if (typeof cb === "function") {
            this.stopCallback = cb;
        }
    }

    // Optional helper to expose current status of provider
    getStatus() {
        return this.provider.getStatus?.();
    }

    // Returns the final cleaned transcript
    getTranscript() {
        return this.transcriptText.trim();
    }
}
